// New case modal. Title/description/module/submodule/type/priority/
// environment, with AI assistance while the user types (debounced 600ms):
//   - /api/qc/suggest/module    → pre-fills an EMPTY module field, marked
//     "AI suggested", freely overridable.
//   - /api/qc/suggest/duplicates → "This looks similar to existing cases"
//     callout with up to 3 cards; each can switch this modal into the
//     attach-to-existing flow instead of filing a new case.
//
// Contract note: the duplicates endpoint REQUIRES module_id, so duplicate
// checking only starts once a module is known (user-picked or AI-suggested).
// Both suggest endpoints may legitimately return nothing — we render nothing.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  QC_CASE_PRIORITIES,
  QC_ENVIRONMENTS,
  type QcCasePriority,
  type QcCaseType,
  type QcDuplicateSuggestion,
  type QcEnvironment,
  type QcModule,
} from '@itms-qc/shared';
import { api, uploadAttachments } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { Modal } from './Modal';
import { AttachmentPicker, appendFiles, type PickedFile } from './AttachmentPicker';
import { IncidentForm } from './IncidentForm';
import { FeatureIntakeChat } from './FeatureIntakeChat';
import { StatusPill, TypeIcon } from './badges';
import {
  dangerTextStyle,
  fieldGap,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  selectStyle,
  textareaStyle,
} from './form';

const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';

interface Props {
  modules: QcModule[];
  onClose: () => void;
  /** New case created → caller navigates to it. */
  onCreated: (caseId: number) => void;
  /** Incident attached to an existing case → caller navigates to it. */
  onAttached: (caseId: number) => void;
  /**
   * Deep-link pre-fill (?report=1&module=<wise-iam id or QC slug>&env=prod):
   * another app's "Report an issue" button lands here with ITS module already
   * picked, so the person never meets a blank picker. A pre-picked module
   * counts as user-chosen — the AI suggestion must not override it.
   */
  initialModuleKey?: string | null;
  initialEnvironment?: QcEnvironment | null;
  /** Recorded on the incident as its `source` ('deep-link'); default 'manual'. */
  source?: string | null;
}

export function NewCaseModal({
  modules,
  onClose,
  onCreated,
  onAttached,
  initialModuleKey = null,
  initialEnvironment = null,
  source = null,
}: Props) {
  const getToken = useApiToken();

  // --- form state ---
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<QcCaseType>('bug');
  const [moduleId, setModuleId] = useState<number | ''>('');
  const [submoduleId, setSubmoduleId] = useState<number | ''>('');
  const [priority, setPriority] = useState<QcCasePriority>('medium');
  const [environment, setEnvironment] = useState<QcEnvironment | ''>(initialEnvironment ?? '');
  // Has the user explicitly chosen a module? AI only fills while untouched.
  const [moduleTouched, setModuleTouched] = useState(false);
  // Module value currently comes from the AI (renders the "AI suggested" tag).
  const [moduleFromAi, setModuleFromAi] = useState(false);

  // The module list loads asynchronously and the modal may already be open
  // (the ?report=1 deep link opens it on first paint), so resolve the
  // pre-picked module whenever the list arrives — once, and only while the
  // field is still untouched.
  useEffect(() => {
    if (!initialModuleKey || moduleTouched || moduleId !== '') return;
    const key = initialModuleKey.toLowerCase();
    const match = modules.find((m) => m.iamModuleId === key) ?? modules.find((m) => m.slug === key);
    if (!match) return;
    setModuleId(match.id);
    setSubmoduleId('');
    setModuleTouched(true);
    setModuleFromAi(false);
  }, [modules, initialModuleKey, moduleTouched, moduleId]);

  // Group the picker by suite — 38 modules in one flat list is not a picker.
  const modulesBySuite = useMemo(() => {
    const groups = new Map<string, QcModule[]>();
    for (const m of modules) {
      const list = groups.get(m.suite) ?? [];
      list.push(m);
      groups.set(m.suite, list);
    }
    return [...groups.entries()];
  }, [modules]);

  // --- guided feature-request intake ---
  // guided = the AI conversation is on screen; guidedSkipped = the user opted
  // out (don't auto-restart); descriptionFromAi = the description came from the
  // guided synthesis (renders an "AI-assisted" tag, freely editable).
  const [guided, setGuided] = useState(false);
  const [guidedSkipped, setGuidedSkipped] = useState(false);
  const [descriptionFromAi, setDescriptionFromAi] = useState(false);

  function chooseType(next: QcCaseType) {
    setType(next);
    // Guided-by-default: picking "Feature request" drops the requestor into the
    // intake conversation — unless they've already skipped it or AI-drafted a
    // description, and only once there's a title to work from.
    if (next === 'feature_request' && !guidedSkipped && !descriptionFromAi && title.trim()) {
      setGuided(true);
    }
    if (next === 'bug') setGuided(false);
  }

  // --- AI suggestion state ---
  const [duplicates, setDuplicates] = useState<QcDuplicateSuggestion[]>([]);
  const [checking, setChecking] = useState(false);
  const seqRef = useRef(0);

  // --- attach-to-existing switch ---
  const [attachTarget, setAttachTarget] = useState<QcDuplicateSuggestion | null>(null);

  // --- attachments (multiple, stored as qc_incidents.attachment_urls) ---
  const [files, setFiles] = useState<PickedFile[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paste a screenshot straight into the description — same convenience as
  // the helpdesk new-ticket page. Pasted images are appended to the list.
  function onPasteImage(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    if (!item) return; // plain text paste — let it through
    const pasted = item.getAsFile();
    if (!pasted) return;
    e.preventDefault();
    const ext = (pasted.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    const named = new File(
      [pasted],
      `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${ext}`,
      { type: pasted.type },
    );
    setFiles((prev) => appendFiles(prev, [named]));
  }

  const selectedModule = modules.find((m) => m.id === moduleId);
  const submodules = selectedModule?.submodules.filter((s) => s.active) ?? [];

  // Debounced AI assistance — 600ms after the last keystroke in title or
  // description (module changes also re-run so duplicates re-scope).
  useEffect(() => {
    const t = title.trim();
    const d = description.trim();
    if (t.length + d.length < 12) {
      setDuplicates([]);
      return;
    }
    const handle = window.setTimeout(() => {
      const seq = ++seqRef.current;
      setChecking(true);
      void (async () => {
        try {
          const token = await getToken();
          const [dupRes, modRes] = await Promise.all([
            typeof moduleId === 'number'
              ? api
                  .suggestDuplicates(token, { title: t, description: d, moduleId })
                  .catch(() => ({ suggestions: [] as QcDuplicateSuggestion[] }))
              : Promise.resolve({ suggestions: [] as QcDuplicateSuggestion[] }),
            api.suggestModule(token, { title: t, description: d }).catch(() => ({ suggestion: null })),
          ]);
          if (seq !== seqRef.current) return; // stale response — a newer keystroke superseded it
          setDuplicates(dupRes.suggestions.slice(0, 3));
          // Pre-fill the module ONLY if the user hasn't picked one.
          if (modRes.suggestion && moduleId === '' && !moduleTouched) {
            setModuleId(modRes.suggestion.moduleId);
            setSubmoduleId(modRes.suggestion.submoduleId ?? '');
            setModuleFromAi(true);
          }
        } finally {
          if (seq === seqRef.current) setChecking(false);
        }
      })();
    }, 600);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, moduleId]);

  async function submit() {
    const t = title.trim();
    const d = description.trim();
    if (!t) return setError('Title is required.');
    if (!d) return setError('Description is required.');
    if (typeof moduleId !== 'number') return setError('Pick the module this is about.');

    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const attachmentUrls = files.length
        ? await uploadAttachments(token, files.map((f) => f.file))
        : [];
      const { case: created } = await api.createCase(token, {
        title: t,
        description: d,
        type,
        moduleId,
        submoduleId: typeof submoduleId === 'number' ? submoduleId : null,
        priority,
        environment: environment || null,
        attachmentUrls,
        source: source ?? undefined,
        sourceModuleSlug: initialModuleKey ?? null,
      });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the case');
      setSubmitting(false);
    }
  }

  // === Attach-to-existing flow (switched into from a duplicate card) ===
  if (attachTarget) {
    return (
      <Modal title="Attach my report" onClose={onClose} maxWidth={560}>
        <div
          style={{
            background: 'var(--brand-bg, #F4F8FB)',
            border: '1px solid var(--brand-border, #D6E4EE)',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: 'var(--brand-text, #001C3E)',
          }}
        >
          Adding your report to{' '}
          <span style={{ fontFamily: MONO, fontSize: 12 }}>{attachTarget.caseNumber}</span>{' '}
          <strong>{attachTarget.title}</strong>
        </div>
        <IncidentForm
          caseId={attachTarget.caseId}
          initialDescription={description}
          submitLabel="Attach my report"
          onDone={() => onAttached(attachTarget.caseId)}
        />
        <button
          type="button"
          onClick={() => setAttachTarget(null)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--brand-text-muted, #8BA4B8)',
            cursor: 'pointer',
            fontSize: 12,
            textDecoration: 'underline',
            padding: 0,
            marginTop: 14,
          }}
        >
          ← Back — file a new case instead
        </button>
      </Modal>
    );
  }

  return (
    <Modal title="New case" onClose={onClose} maxWidth={680}>
      <div style={fieldGap}>
        <label style={labelStyle}>
          Title
          <input
            autoFocus
            type="text"
            value={title}
            maxLength={256}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="One line — what's broken or what you want"
            style={{ ...inputStyle, marginTop: 4 }}
          />
        </label>
      </div>

      <div style={fieldGap}>
        <label style={labelStyle}>What is this?</label>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {(['bug', 'feature_request'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => chooseType(t)}
              style={{
                ...secondaryButtonStyle,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                borderColor: type === t ? 'var(--brand-primary, #001C3E)' : 'var(--brand-border, #D6E4EE)',
                background: type === t ? 'var(--brand-primary, #001C3E)' : 'transparent',
                color: type === t ? 'var(--brand-text-on-primary, white)' : 'var(--brand-text, #001C3E)',
              }}
            >
              <TypeIcon type={t} size={14} />
              {t === 'bug' ? 'Bug' : 'Feature request'}
            </button>
          ))}
        </div>
      </div>

      {/* Guided feature-request intake replaces the description box while active */}
      {type === 'feature_request' && guided ? (
        <FeatureIntakeChat
          title={title}
          moduleName={selectedModule?.displayName ?? null}
          initialAnswer={description}
          onComplete={(desc) => {
            setDescription(desc);
            setDescriptionFromAi(true);
            setGuided(false);
          }}
          onSkip={() => {
            setGuided(false);
            setGuidedSkipped(true);
          }}
        />
      ) : (
        <div style={fieldGap}>
          <label style={labelStyle}>
            Description
            {descriptionFromAi && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  fontWeight: 500,
                  background: '#EEEDFE',
                  color: '#3C3489',
                  borderRadius: 4,
                  padding: '1px 6px',
                  verticalAlign: 'middle',
                }}
                title="Drafted with the AI intake assistant — edit it freely"
              >
                ✦ AI-assisted
              </span>
            )}
            {type === 'feature_request' && !descriptionFromAi && (
              <button
                type="button"
                onClick={() => {
                  if (!title.trim()) {
                    setError('Add a title first, then I can help you describe it.');
                    return;
                  }
                  setGuidedSkipped(false);
                  setGuided(true);
                }}
                style={{
                  marginLeft: 8,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  color: '#3C3489',
                  fontWeight: 500,
                  padding: 0,
                  verticalAlign: 'middle',
                }}
              >
                ✦ Help me describe this
              </button>
            )}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={onPasteImage}
              placeholder="Steps to reproduce, what you expected, what happened instead… Paste a screenshot directly, or attach a file below."
              style={{ ...textareaStyle, marginTop: 4, minHeight: 110 }}
            />
          </label>
        </div>
      )}

      <div style={fieldGap}>
        <AttachmentPicker files={files} setFiles={setFiles} disabled={submitting} />
      </div>

      {/* AI duplicate callout — render nothing when there's nothing to show */}
      {checking && duplicates.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', marginBottom: 12 }}>
          Checking for similar cases…
        </div>
      )}
      {duplicates.length > 0 && (
        <div
          style={{
            border: '1px solid #E8D9B5',
            background: '#FDF8EC',
            borderRadius: 6,
            padding: '12px 14px',
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6B4E1B', marginBottom: 10 }}>
            This looks similar to existing cases
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {duplicates.map((dup) => (
              <div
                key={dup.caseId}
                className="qc-wrap-row"
                style={{
                  background: 'var(--brand-card-bg, white)',
                  border: '1px solid var(--brand-border, #D6E4EE)',
                  borderRadius: 6,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}>
                      {dup.caseNumber}
                    </span>
                    <StatusPill status={dup.status} />
                    <span style={{ fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}>
                      {dup.incidentCount} incident{dup.incidentCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--brand-text, #001C3E)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={dup.reasoning}
                  >
                    {dup.title}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachTarget(dup)}
                  style={{ ...secondaryButtonStyle, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Attach my report to this case
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="qc-field-row" style={{ display: 'flex', gap: 12, ...fieldGap }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={labelStyle}>
            Module
            {moduleFromAi && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  fontWeight: 500,
                  background: '#EEEDFE',
                  color: '#3C3489',
                  borderRadius: 4,
                  padding: '1px 6px',
                  verticalAlign: 'middle',
                }}
                title="Suggested by AI from your title and description — change it freely"
              >
                ✦ AI suggested
              </span>
            )}
            <select
              value={moduleId}
              onChange={(e) => {
                setModuleId(e.target.value ? Number(e.target.value) : '');
                setSubmoduleId('');
                setModuleTouched(true);
                setModuleFromAi(false);
              }}
              style={{ ...selectStyle, marginTop: 4 }}
            >
              <option value="">Select module…</option>
              {modulesBySuite.map(([suite, list]) => (
                <optgroup key={suite} label={suite}>
                  {list.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={labelStyle}>
            Submodule
            <select
              value={submoduleId}
              onChange={(e) => setSubmoduleId(e.target.value ? Number(e.target.value) : '')}
              disabled={submodules.length === 0}
              style={{ ...selectStyle, marginTop: 4, opacity: submodules.length === 0 ? 0.6 : 1 }}
            >
              <option value="">{submodules.length === 0 ? '—' : 'None'}</option>
              {submodules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="qc-field-row" style={{ display: 'flex', gap: 12, ...fieldGap }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>
            Priority
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as QcCasePriority)}
              style={{ ...selectStyle, marginTop: 4 }}
            >
              {QC_CASE_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ width: 140 }}>
          <label style={labelStyle}>
            Environment
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as QcEnvironment | '')}
              style={{ ...selectStyle, marginTop: 4 }}
            >
              <option value="">Not sure</option>
              {QC_ENVIRONMENTS.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div style={{ ...dangerTextStyle, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          style={{ ...primaryButtonStyle, opacity: submitting ? 0.6 : 1 }}
        >
          {submitting ? 'Creating…' : 'Create case'}
        </button>
      </div>
    </Modal>
  );
}
